import { useEffect, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { collectFingerprint, requestMagicLink } from '../lib/api';
import { useAuth } from '../lib/auth';

// /partner/login — sibling of /login but locked to the partner audience.
// The magic-link request carries partnerOnly:true so the verify endpoint
// refuses to mint a session for a plain user. The OAuth links append
// ?partner=1 so the callback applies the same gate via a state cookie.
//
// On `?error=not_a_partner` we render an inline banner — the OAuth round
// trip lands here after the gate rejects, and the SPA reads the flag.
export function PartnerLogin() {
  const { t, locale, localePath } = useI18n();
  const { status: authStatus, user } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sent' | 'error'>('idle');
  const [reject, setReject] = useState(false);

  useEffect(() => {
    const qs = new URL(location.href).searchParams;
    if (qs.get('error') === 'not_a_partner') {
      setReject(true);
    }
  }, []);

  // Already a partner/admin → straight to /partner. Plain users stay on the
  // login page so the inline reject banner can render.
  useEffect(() => {
    if (authStatus !== 'signed-in' || !user) return;
    if (user.role === 'partner' || user.role === 'admin') {
      location.replace(localePath('/partner'));
    }
  }, [authStatus, user]);

  function oauthHref(path: string): string {
    return `${location.origin}${path}?partner=1`;
  }

  function openOAuth(e: Event, href: string) {
    e.preventDefault();
    location.assign(href);
  }

  async function onSubmit(e: Event) {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setReject(false);
    try {
      await requestMagicLink(email.trim(), locale, collectFingerprint(), { partnerOnly: true });
      setStatus('sent');
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div class="login-page">
      <div class="login-card">
        <h1>
          {t('login.title_a')} <em>{t('partnerLogin.title')}</em>
        </h1>
        <p class="lede">{t('partnerLogin.lede')}</p>

        {reject && (
          <div class="login-reject" role="alert">
            <strong>{t('partnerLogin.reject.title')}</strong>
            <p>{t('partnerLogin.reject.body')}</p>
          </div>
        )}

        {authStatus === 'signed-in' && user && user.role !== 'partner' && user.role !== 'admin' && (
          <div class="login-reject" role="alert">
            <strong>{t('partnerLogin.signedInUser.title')}</strong>
            <p>
              {t('partnerLogin.signedInUser.body').replace('{email}', user.email)}
            </p>
          </div>
        )}

        <form onSubmit={onSubmit}>
          <label for="email">{t('login.email_label')}</label>
          <input
            id="email"
            type="email"
            placeholder={t('login.email_placeholder')}
            value={email}
            required
            disabled={status === 'sent'}
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
          />
          <button type="submit" class="primary" disabled={loading || status === 'sent'}>
            {t('login.email_cta')}
          </button>
        </form>

        {status === 'sent' && <p class="login-status">{t('login.email_sent')}</p>}
        {status === 'error' && <p class="login-status err">{t('login.error')}</p>}

        <div class="login-divider">{t('login.or')}</div>

        <div class="login-oauth">
          <a href={oauthHref('/api/auth/google')} onClick={(e) => openOAuth(e, oauthHref('/api/auth/google'))}>
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.1 8 3l5.7-5.7C34.7 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.6 15.2 18.9 12 24 12c3.1 0 5.9 1.1 8 3l5.7-5.7C34.7 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.1z"/>
              <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.4l-6.5-5.5C29.6 34.9 26.9 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.3 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.5 5.5C41.1 35.6 44 30.3 44 24c0-1.3-.1-2.4-.4-3.5z"/>
            </svg>
            {t('login.google')}
          </a>
          <a href={oauthHref('/api/auth/apple')} class="apple" onClick={(e) => openOAuth(e, oauthHref('/api/auth/apple'))}>
            <svg width="14" height="16" viewBox="0 0 22 26" aria-hidden="true">
              <path fill="#fff" d="M17.6 13.6c0-3 2.5-4.5 2.6-4.6-1.4-2.1-3.6-2.4-4.4-2.4-1.9-.2-3.6 1.1-4.6 1.1-1 0-2.4-1.1-4-1.1-2 0-3.9 1.2-4.9 3C.2 12.3 1.7 18 3.7 21c1 1.5 2.2 3.2 3.7 3.1 1.5-.1 2.1-1 3.9-1 1.8 0 2.3 1 3.9 1 1.6 0 2.7-1.5 3.7-3 1.2-1.7 1.6-3.4 1.7-3.5-.1 0-3.2-1.3-3.3-5z"/>
              <path fill="#fff" d="M14.7 4.5c.8-1 1.4-2.4 1.3-3.8-1.2.1-2.7.8-3.5 1.8-.8.9-1.5 2.3-1.3 3.7 1.4.1 2.7-.7 3.5-1.7z"/>
            </svg>
            {t('login.apple')}
          </a>
        </div>

        <a class="login-back" href={localePath()}>← BeerCup</a>
      </div>
    </div>
  );
}
